import React, { useEffect, useMemo, useState } from 'react';
import Text from 'components/Text/Text';
import { ArchivedLabel } from 'components/Select/ArchivedLabel';
import { SHOW_ARCHIVED_RESTAURANTS } from 'appConstants';
import SearchField from 'components/SearchField/SearchField';
import OutsideFormSelect from 'components/Select/OutsideFormSelect';
import { Checkbox } from 'antd';
import { IItemForGetShortInfoWithStatus, EntityStatus } from 'types';
import { ISelectOption } from 'components/Select/types';
import style from './MultipleSelect.module.scss';

interface IProps {
	data: IItemForGetShortInfoWithStatus[];
	isLoading: boolean;
	refetch: () => void;
	responseError: boolean;
	isApplyToOne: boolean;
	setAdditionalRestaurants: (value: string[]) => void;
	selectedAdditionalRestaurants: string[];
	isVisibleAdditionalRestaurantsModal: boolean;
}

export const MultipleSelect: React.FC<IProps> = React.memo(
	({
		data,
		isLoading,
		refetch,
		responseError,
		isApplyToOne,
		setAdditionalRestaurants,
		selectedAdditionalRestaurants,
		isVisibleAdditionalRestaurantsModal,
	}) => {
		const [searchString, setSearchString] = useState<string>('');
		const [isSelectedAllRestaurants, setIsSelectedAllRestaurants] = useState<boolean>(false);
		const [showArchived, setShowArchived] = useState<boolean>(
			localStorage.getItem(SHOW_ARCHIVED_RESTAURANTS) === 'true',
		);

		const visibleData = useMemo(() => {
			return showArchived
				? data
				: data.filter(
						(el) => el.status === EntityStatus.Active || selectedAdditionalRestaurants.includes(el.id),
				  );
		}, [data, showArchived, selectedAdditionalRestaurants]);

		const filteredRestaurants = useMemo(() => {
			return searchString
				? visibleData.filter((el) => el.name.toLowerCase().includes(searchString.toLowerCase()))
				: visibleData;
		}, [searchString, visibleData]);

		const mappedOptions = useMemo<ISelectOption[]>(() => {
			return filteredRestaurants.map((el) => ({
				id: el.id,
				name: el.name,
				additionalContent: el.status === EntityStatus.Archived ? <ArchivedLabel /> : undefined,
			}));
		}, [filteredRestaurants]);

		const handleSelectAllRestaurants = () => {
			if (isSelectedAllRestaurants) {
				setAdditionalRestaurants([]);
			} else {
				const IDsOfAllRestaurants = visibleData.map((el) => el.id);
				setAdditionalRestaurants(IDsOfAllRestaurants);
			}
		};

		const toggleShowArchived = () => {
			localStorage.setItem(SHOW_ARCHIVED_RESTAURANTS, JSON.stringify(!showArchived));
			setShowArchived((prevValue) => !prevValue);
		};

		useEffect(() => {
			setSearchString('');
		}, [isVisibleAdditionalRestaurantsModal]);

		useEffect(() => {
			if (selectedAdditionalRestaurants.length === 0) {
				setIsSelectedAllRestaurants(false);
			} else if (selectedAdditionalRestaurants.length === visibleData.length) {
				setIsSelectedAllRestaurants(true);
			} else if (selectedAdditionalRestaurants.length !== visibleData.length) {
				setIsSelectedAllRestaurants(false);
			}
		}, [selectedAdditionalRestaurants.length, visibleData.length]);

		const renderSelectMenu = (menu: React.ReactElement) => {
			return (
				<>
					<div className={style.selectMenuHeader}>
						<Checkbox
							className={style.checkBox}
							checked={isSelectedAllRestaurants}
							disabled={!visibleData.length}
							onChange={handleSelectAllRestaurants}>
							<Text
								translationNameSpace="form"
								translationKey="selectAnyItemPlaceholder"
								asFragment={true}
							/>
						</Checkbox>
						<Checkbox className={style.checkBox} checked={showArchived} onChange={toggleShowArchived}>
							<Text translationNameSpace="modals" translationKey="showArchived" asFragment={true} />
						</Checkbox>
						<SearchField
							onChangeSelectSearchString={setSearchString}
							searchString={searchString}
							className={style.searchField}
						/>
					</div>
					{menu}
				</>
			);
		};

		return (
			<OutsideFormSelect
				testId="MultipleSelect"
				name="IDsSelectedRestaurantsForChangeDishPosition"
				label="restaurantsLabel"
				value={selectedAdditionalRestaurants}
				options={mappedOptions}
				fetchError={responseError}
				loading={isLoading}
				onRefetch={refetch}
				disabled={isApplyToOne || isLoading}
				onChange={setAdditionalRestaurants}
				counterValue={selectedAdditionalRestaurants.length}
				withValidation={false}
				dropdownRender={renderSelectMenu}
				mode="multiple"
			/>
		);
	},
);
